import { useState, useEffect, useCallback, useRef } from "react";

import { formatDistanceToNow } from "date-fns";
import { Bell, X, Check, CheckCheck } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { useAuth } from "@/contexts/AuthContext";
import { useRealtime } from "@/contexts/RealtimeContext";
import { useRealtimeChannel } from "@/hooks/useRealtimeChannel";
import { hashIdentifier } from "@/lib/authHelpers";
import { supabase } from "@/integrations/supabase/client";

interface Notification {
  id: string;
  type: string;
  title: string;
  message: string;
  icon: string | null;
  is_read: boolean;
  action_url: string | null;
  created_at: string;
}

export const NotificationCenter = () => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [channelName, setChannelName] = useState<string | null>(null);
  const { status: realtimeStatus } = useRealtime();
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Generate hashed channel name when user is available
  useEffect(() => {
    if (user?.id) {
      hashIdentifier(user.id).then(hash => {
        setChannelName(`private-notifications:${hash}`);
      });
    }
  }, [user?.id]);

  // Fetch initial notifications
  const fetchNotifications = useCallback(async () => {
    if (!user) return;

    const { data, error } = await supabase
      .from('user_notifications')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(50);

    if (!error && data) {
      setNotifications(data);
      setUnreadCount(data.filter(n => !n.is_read).length);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchNotifications();
    }
  }, [user, fetchNotifications]);

  // Handle incoming real-time notifications
  const handleNewNotification = useCallback((payload: any) => {
    const newNotif = payload.new as Notification;
    setNotifications(prev => [newNotif, ...prev]);
    setUnreadCount(prev => prev + 1);
  }, []);

  // Subscribe to real-time notifications using the new hook
  const isRealtimeConnected = realtimeStatus === 'connected';
  useRealtimeChannel({
    channelName: isRealtimeConnected && channelName ? channelName : '',
    type: 'postgres_changes',
    event: 'INSERT',
    schema: 'public',
    table: 'user_notifications',
    filter: user?.id ? `user_id=eq.${user.id}` : undefined,
    onMessage: handleNewNotification,
  });

  // Set up polling as a fallback when Realtime is disconnected
  useEffect(() => {
    if (realtimeStatus === 'disconnected') {
      console.log('[NotificationCenter] Realtime disconnected, starting polling for notifications.');
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
      pollingIntervalRef.current = setInterval(fetchNotifications, 60000); // Poll every minute
    } else {
      if (pollingIntervalRef.current) {
        console.log('[NotificationCenter] Realtime (re)connected, stopping polling.');
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    }

    return () => {
      if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
    };
  }, [realtimeStatus, fetchNotifications]);

  const markAsRead = async (notificationId: string) => {
    await supabase
      .from('user_notifications')
      .update({ is_read: true })
      .eq('id', notificationId);

    setNotifications(prev =>
      prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
    );
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  const markAllAsRead = async () => {
    if (!user) return;

    await supabase
      .from('user_notifications')
      .update({ is_read: true })
      .eq('user_id', user.id)
      .eq('is_read', false);

    setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    setUnreadCount(0);
  };

  const deleteNotification = async (notificationId: string) => {
    await supabase
      .from('user_notifications')
      .delete()
      .eq('id', notificationId);

    setNotifications(prev => prev.filter(n => n.id !== notificationId));
    const notif = notifications.find(n => n.id === notificationId);
    if (notif && !notif.is_read) {
      setUnreadCount(prev => Math.max(0, prev - 1));
    }
  };

  const getNotificationColor = (type: string) => {
    switch (type) {
      case 'streak_warning':
        return 'border-l-4 border-l-orange-500';
      case 'streak_milestone':
        return 'border-l-4 border-l-orange-500';
      case 'achievement':
        return 'border-l-4 border-l-primary';
      case 'level_up':
        return 'border-l-4 border-l-green-500';
      case 'goal_complete':
        return 'border-l-4 border-l-blue-500';
      default:
        return '';
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <Badge
              variant="destructive"
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </Badge>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent className="w-full sm:max-w-md">
        <SheetHeader>
          <div className="flex items-center justify-between">
            <SheetTitle>Notifications</SheetTitle>
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={markAllAsRead}
                className="text-xs"
              >
                <CheckCheck className="h-4 w-4 mr-1" />
                Mark all read
              </Button>
            )}
          </div>
          <SheetDescription>
            {unreadCount === 0
              ? "You're all caught up!"
              : `${unreadCount} unread notification${unreadCount > 1 ? 's' : ''}`}
          </SheetDescription>
        </SheetHeader>

        <ScrollArea className="h-[calc(100vh-180px)] mt-6">
          {notifications.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Bell className="h-12 w-12 mx-auto mb-3 opacity-20" />
              <p>No notifications yet</p>
            </div>
          ) : (
            <div className="space-y-2">
              {notifications.map((notif) => (
                <div
                  key={notif.id}
                  className={`relative p-3 rounded-lg border ${
                    !notif.is_read ? 'bg-primary/5' : 'bg-muted/30'
                  } ${getNotificationColor(notif.type)}`}
                >
                  <div className="flex items-start gap-3">
                    {notif.icon && (
                      <div className="text-2xl shrink-0">{notif.icon}</div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <p className="font-medium text-sm">{notif.title}</p>
                        {!notif.is_read && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 shrink-0"
                            onClick={() => markAsRead(notif.id)}
                          >
                            <Check className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground mb-2">
                        {notif.message}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {formatDistanceToNow(new Date(notif.created_at), { addSuffix: true })}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 shrink-0"
                      onClick={() => deleteNotification(notif.id)}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
};
